<template>
  <SmTable
    :data="data"
    :columns="columns"
    :loading="loading"
    key-field="id"
    class="h-full"
    @row-click="handleRowClick">
    <template #id="{ row }">
      <div class="flex items-center gap-3">
        <div
          class="bg-blue-50 dark:bg-blue-900/30 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
          <AppWindow class="text-blue-600 dark:text-blue-400 h-4 w-4" />
        </div>
        <span class="font-medium">{{ row.id }}</span>
      </div>
    </template>

    <template #users="{ row }">
      <div class="flex items-center justify-end gap-2">
        <span
          class="text-gray-500 dark:text-gray-400"
          :aria-label="i18n.t('browserExtension.activeUsers')">
          <Users :size="16" />
        </span>
        <span>{{ row.metadata.unique_installations }}</span>
      </div>
    </template>

    <template #lastUsage="{ row }">
      <template v-if="getLastUsage(row) > 0">
        <SmTooltip>
          {{ dayjs().to(dayjs.unix(getLastUsage(row))) }}
          <template #content>
            <span class="whitespace-nowrap">{{
              displayDate(new Date(getLastUsage(row) * 1000))
            }}</span>
          </template>
        </SmTooltip>
      </template>
      <template v-else>-</template>
    </template>

    <template #no-data>
      <EmptyState
        :icon="AppWindow"
        :title="i18n.t('browserExtension.noToolsFound')"
        :text="i18n.t('browserExtension.noToolsFoundDescription')"
        :show-create-btn="false"
        :show-docs-btn="false" />
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { AppWindow, Users } from 'lucide-vue-next'
  import type { Column } from '@/components/sm/SmTable.types'
  import type { UsageStats } from '@/client'
  import { capitalize } from '@/utils/formatter/formatter'
  import { displayDate } from '@/utils/formatter/date'
  import { compareNumbers } from '@/utils/sort'
  import SmTooltip from '@/components/sm/SmTooltip.vue'
  import dayjs from 'dayjs'
  import relativeTime from 'dayjs/plugin/relativeTime'

  dayjs.extend(relativeTime)

  interface Props {
    data: UsageStats[]
    loading?: boolean
  }

  withDefaults(defineProps<Props>(), {
    loading: false,
  })

  const emit = defineEmits<{
    'row-click': [iRow: number, row: UsageStats]
  }>()

  const i18n = useI18n()

  const columns = computed<Column<UsageStats>[]>(() => [
    {
      key: 'id',
      label: capitalize(i18n.t('browserExtension.softwareName')),
      width: 2,
      sortable: true,
    },
    {
      key: 'users',
      label: capitalize(i18n.t('browserExtension.activeUsers')),
      width: 1,
      sortable: true,
      sortFn: function (a, b) {
        return compareNumbers(
          Number(a.metadata?.unique_installations || 0),
          Number(b.metadata?.unique_installations || 0)
        )
      },
      align: 'right',
    },
    {
      key: 'lastUsage',
      label: capitalize(i18n.t('lastUsage')),
      width: 1,
      sortable: true,
      sortFn: function (a, b) {
        return compareNumbers(getLastUsage(a), getLastUsage(b))
      },
    },
  ])

  function getLastUsage(item: UsageStats): number {
    if (!item.daily_usages || item.daily_usages.length === 0) return 0
    return item.daily_usages[item.daily_usages.length - 1]?.timestamp || 0
  }

  function handleRowClick(iRow: number, row: UsageStats) {
    emit('row-click', iRow, row)
  }

  // Expose helper functions for external use if needed
  defineExpose({
    getLastUsage,
  })
</script>
